import { deleteSalesTaxFiling } from '@commenda/api/new-sales-tax/filing';
import {
  Badge,
  BadgeProps,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  IconButton,
  Icons,
} from '@commenda/kakashi';
import { classNames } from '@commenda/utils';
import { useState } from 'react';
import { useNotification } from '../../../../../hooks/useNotification';
import { FILING_FREQUENCY_LABEL_MAP } from '../../constants';
import { CalendarPreviewHelper } from '../../helpers';
import { FilingInfoCardProps } from '../../types';

const CARD_COLORS = {
  new: {
    bgColor: 'bg-utility-blue-500',
    borderColor: 'border-utility-blue-500',
    borderLeftColor: 'border-l-utility-blue-300',
    titleColor: 'text-white',
    textColor: 'text-utility-blue-100',
    subtextColor: 'text-utility-blue-200',
    separatorColor: 'bg-utility-blue-200',
  },
  existing: {
    bgColor: 'bg-utility-gray-50',
    borderColor: 'border-utility-gray-200',
    borderLeftColor: 'border-l-utility-gray-500',
    titleColor: 'text-text-primary',
    textColor: 'text-text-secondary',
    subtextColor: 'text-text-tertiary',
    separatorColor: 'bg-text-tertiary',
  },
  filed: {
    bgColor: 'bg-utility-success-50',
    borderColor: 'border-utility-success-200',
    borderLeftColor: 'border-l-utility-success-500',
    titleColor: 'text-text-primary',
    textColor: 'text-text-secondary',
    subtextColor: 'text-text-tertiary',
    separatorColor: 'bg-text-tertiary',
  },
  'to-delete': {
    bgColor: 'bg-utility-error-100',
    borderColor: 'border-utility-error-200',
    borderLeftColor: 'border-l-utility-error-500',
    titleColor: 'text-text-primary',
    textColor: 'text-text-secondary',
    subtextColor: 'text-text-tertiary',
    separatorColor: 'bg-text-tertiary',
  },
};

// Badge config by effective variant
const BADGE_CONFIG: Record<
  string,
  { label: string; variant: BadgeProps['variant'] }
> = {
  new: { label: 'New', variant: 'info-solid' },
  existing: { label: 'Existing', variant: 'blue' },
  filed: { label: 'Filed', variant: 'success' },
};

type CardVariant = keyof typeof CARD_COLORS;

export function FilingInfoCard({
  filing,
  variant = 'new',
  rowSpan = 1,
  onDelete,
  corporationId,
}: FilingInfoCardProps & { rowSpan?: number }) {
  const { setNotification } = useNotification();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Determine effective variant. `variant` comes typed as 'new' | 'existing'
  // | 'to-delete' from the props; all three are keys of CARD_COLORS so we can
  // widen directly without any assertion.
  const inputVariant: CardVariant = variant;
  let effectiveVariant: CardVariant = inputVariant;

  if (filing.status === 'FILED') {
    effectiveVariant = 'filed';
  } else if (variant === 'new' && filing.id) {
    effectiveVariant = 'existing';
  }

  const colors = CARD_COLORS[effectiveVariant];
  const badge = BADGE_CONFIG[effectiveVariant];

  const showDelete =
    effectiveVariant === 'existing' &&
    filing.status !== 'FILED' &&
    onDelete &&
    corporationId != null &&
    filing.id;

  const handleConfirmDelete = async () => {
    if (!filing.id || corporationId == null) return;
    setIsDeleting(true);
    const result = await deleteSalesTaxFiling(filing.id, corporationId);
    setIsDeleting(false);
    if (result.success) {
      setDeleteDialogOpen(false);
      onDelete?.(filing);
      setNotification({
        show: true,
        title: 'Filing deleted',
        valid: true,
      });
    } else {
      setNotification({
        show: true,
        title: result.error ?? 'Failed to delete filing',
        valid: false,
      });
    }
  };

  return (
    <>
      <div
        className={classNames(
          // ring + shadow separates adjacent cards. overflow-hidden prevents
          // content from bleeding outside the card (into the next row) when
          // the row height is shorter than the natural content height.
          'rounded-lg border-l-4 p-md h-full relative ring-1 ring-white/70 shadow-sm overflow-hidden',
          colors.bgColor,
          colors.borderColor,
          colors.borderLeftColor,
          'border'
        )}
      >
        {showDelete && (
          <div className="absolute top-sm right-sm">
            <IconButton
              size="sm"
              variant="destructive"
              icon={<Icons.Trash01 className="size-4" />}
              aria-label="Delete filing"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteDialogOpen(true);
              }}
            />
          </div>
        )}
        <div className="flex flex-col gap-xs">
          <div className="flex items-center gap-sm flex-wrap">
            <h4
              className={classNames(
                'txt-extra-small-bold truncate',
                colors.titleColor
              )}
            >
              {filing.form_title}
            </h4>

            <div
              className={classNames(
                'size-1 rounded-full',
                colors.separatorColor
              )}
            />
            <p
              className={classNames(
                'txt-extra-small-regular',
                colors.textColor
              )}
            >
              {FILING_FREQUENCY_LABEL_MAP[filing.filing_frequency]}
            </p>

            {badge && (
              <Badge variant={badge.variant} size="sm">
                {badge.label}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-sm flex-wrap">
            <p
              className={classNames(
                'txt-extra-small-regular',
                colors.subtextColor
              )}
            >
              Due on {CalendarPreviewHelper.formatDateForDisplay(filing.due_date)}
            </p>
            <div
              className={classNames(
                'size-1 rounded-full',
                colors.separatorColor
              )}
            />
            <p
              className={classNames(
                'txt-extra-small-regular',
                colors.subtextColor
              )}
            >
              {effectiveVariant === 'existing' ? 'Tax Period: ' : 'Period: '}
              {CalendarPreviewHelper.formatPeriodRange(
                filing.reporting_period_start_date,
                filing.reporting_period_end_date
              )}
            </p>
          </div>
        </div>
      </div>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="p-none flex flex-col gap-xl min-w-[400px]">
          <div className="p-3xl pb-none">
            <div className="size-12 rounded-md bg-utility-error-50 flex justify-center items-center">
              <Icons.AlertCircle className="size-6 text-utility-error-600" />
            </div>
          </div>
          <div className="px-3xl">
            <h3 className="txt-lg-bold text-text-primary mb-sm">
              Delete this filing?
            </h3>
            <p className="txt-small-regular text-text-secondary">
              This filing will be removed from the calendar and from Service
              Requests. This cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive-primary"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
